import GermanDateInput from "@/components/GermanDateInput";
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { WIZARD_STEPS, WizardStepper, WizardReview, validateWizardStep } from "@/components/ClientWizardParts";

const todayIso = () => new Date().toISOString().slice(0, 10);
const sectionsUeSum = (sections) => sections.reduce((sum, s) => sum + (Number(s.ue_target) || 0), 0);
const computeTotalUe = (sections, onboardingUe, offboardingUe) =>
  sectionsUeSum(sections) + (Number(onboardingUe) || 0) + (Number(offboardingUe) || 0);

export default function ClientDialog({ open, onOpenChange, allCoaches, allStaff, currentUserId, isAdmin, initialData, onSaved }) {
  const editing = !!initialData;
  // Alt-Modell (additiv, eigener Preis) nur noch für Bestandskunden — neue Kunden weisen
  // Onboarding/Offboarding erst NACH Vertragsunterschrift in der Kunden-Akte zu (aus dem
  // Coaching-UE-Kontingent, ohne zusätzlichen Preis-Posten).
  const isLegacyOb = editing && !!initialData.ob_legacy_pricing;
  const [measures, setMeasures] = useState([]);
  const [form, setForm] = useState(() => ({
    name: "",
    email: "",
    phone: "",
    street: "",
    postal_code: "",
    city: "",
    customer_number: "",
    measure_number: "",
    start_date: todayIso(),
    end_date: todayIso(),
    total_ue: 4,
    coach_user_ids: [],
    primary_coach_id: "",
    notes: "",
    internal_notes: "",
    measure_id: "",
    sections: [],
    birth_date: "",
    kostentraeger: "",
    arbeitsvermittler_email: "",
    ferien: "",
    gesetzl_feiertage: "",
    capacity_hours_per_week: "",
    onboarding_ue: 2,
    offboarding_ue: 2,
    onboarding_user_id: "",
    offboarding_user_id: "",
    onboarding_price: null,
    offboarding_price: null,
    acquired_by_user_id: "",
  }));
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [step, setStep] = useState(0);
  const show = (n) => editing || step === n;
  const next = () => {
    const msg = validateWizardStep(step, form, isLegacyOb);
    if (msg) { toast.error(msg); return; }
    setStep(step + 1);
  };

  useEffect(() => {
    // Load measures catalog once
    (async () => {
      try {
        const r = await api.get("/avgs/measures");
        setMeasures((r.data || []).filter((m) => m.active));
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          name: initialData.name || "",
          email: initialData.email || "",
          phone: initialData.phone || "",
          street: initialData.street || "",
          postal_code: initialData.postal_code || "",
          city: initialData.city || "",
          customer_number: initialData.customer_number || "",
          measure_number: initialData.measure_number || "",
          start_date: initialData.start_date || todayIso(),
          end_date: initialData.end_date || todayIso(),
          total_ue: initialData.total_ue ?? 0,
          coach_user_ids: initialData.coach_user_ids || [],
          primary_coach_id: initialData.primary_coach_id || (initialData.coach_user_ids || [])[0] || "",
          notes: initialData.notes || "",
          internal_notes: initialData.internal_notes || "",
          measure_id: initialData.measure_id || "",
          sections: initialData.sections || [],
          birth_date: initialData.birth_date || "",
          kostentraeger: initialData.kostentraeger || "",
          arbeitsvermittler_email: initialData.arbeitsvermittler_email || "",
          ferien: initialData.ferien || "",
          gesetzl_feiertage: initialData.gesetzl_feiertage || "",
          capacity_hours_per_week: initialData.capacity_hours_per_week ?? "",
          onboarding_ue: initialData.onboarding_ue ?? 0,
          offboarding_ue: initialData.offboarding_ue ?? 0,
          onboarding_user_id: initialData.onboarding_user_id || "",
          offboarding_user_id: initialData.offboarding_user_id || "",
          onboarding_price: initialData.onboarding_price ?? null,
          offboarding_price: initialData.offboarding_price ?? null,
          acquired_by_user_id: initialData.acquired_by_user_id || "",
        });
      } else {
        setForm({
          name: "",
          email: "",
          phone: "",
          street: "",
          postal_code: "",
          city: "",
          customer_number: "",
          measure_number: "",
          start_date: todayIso(),
          end_date: todayIso(),
          total_ue: 4,
          coach_user_ids: isAdmin ? [] : [currentUserId],
          primary_coach_id: isAdmin ? "" : currentUserId,
          notes: "",
          internal_notes: "",
          measure_id: "",
          sections: [],
          birth_date: "",
          kostentraeger: "",
          arbeitsvermittler_email: "",
          ferien: "",
          gesetzl_feiertage: "",
          capacity_hours_per_week: "",
          onboarding_ue: 0,
          offboarding_ue: 0,
          onboarding_user_id: "",
          offboarding_user_id: "",
          onboarding_price: null,
          offboarding_price: null,
          acquired_by_user_id: "",
        });
      }
      setInviteResult(null);
      setStep(0);
    }
  }, [open, initialData, isAdmin, currentUserId]);

  const pickMeasure = (measure_id) => {
    const m = measures.find((x) => x.measure_id === measure_id);
    if (!m) {
      setForm((f) => ({ ...f, measure_id: "", sections: [], total_ue: computeTotalUe([], isLegacyOb && f.onboarding_ue, isLegacyOb && f.offboarding_ue) }));
      return;
    }
    // Do NOT auto-select any sections. Coach picks the modules/sections
    // explicitly in the checkbox list that appears below.
    const ppu = pricePerUeForMeasure(m);
    setForm((f) => ({
      ...f, measure_id, sections: [], total_ue: computeTotalUe([], isLegacyOb && f.onboarding_ue, isLegacyOb && f.offboarding_ue),
      onboarding_price: ppu != null ? Math.round((Number(f.onboarding_ue) || 0) * ppu * 100) / 100 : f.onboarding_price,
      offboarding_price: ppu != null ? Math.round((Number(f.offboarding_ue) || 0) * ppu * 100) / 100 : f.offboarding_price,
    }));
  };

  // Preis/UE aus dem Katalog für ein Modul (falls hinterlegt) — dann wird der Vertragspreis
  // NICHT manuell eingegeben, sondern automatisch als (Summe UE der Abschnitte) × Preis/UE berechnet.
  const pricePerUeForModule = (measure, moduleName) => {
    const sec = (measure?.sections || []).find((s) => (s.module_name || "— Ohne Modul") === moduleName && s.price_per_ue != null);
    return sec ? Number(sec.price_per_ue) : null;
  };

  // Preis/UE der Maßnahme insgesamt (falls IRGENDEIN Abschnitt einen hinterlegt hat) — für
  // Onboarding/Offboarding, die zu keinem einzelnen Modul gehören, aber trotzdem denselben Satz
  // wie die Maßnahme abrechnen (z. B. Jobcoaching für Kreative 61,51 €, Gründercoaching 88,11 €).
  const pricePerUeForMeasure = (measure) => {
    const sec = (measure?.sections || []).find((s) => s.price_per_ue != null);
    return sec ? Number(sec.price_per_ue) : null;
  };

  // Modulname IMMER live über section_id aus dem aktuellen Katalog auflösen — verhindert, dass
  // ein Abschnitt mit veraltetem, zwischengespeichertem Modulnamen (z. B. vor einer
  // Katalog-Umbenennung angelegt) aus der UE-Summe des Moduls herausfällt.
  const canonicalModuleName = (measure, section) => {
    const cat = (measure?.sections || []).find((s) => s.section_id === section.section_id);
    return cat?.module_name || section.module_name || "— Ohne Modul";
  };

  const recomputeModulePrice = (sections, measure, moduleName) => {
    const grouped = sections.filter((s) => canonicalModuleName(measure, s) === moduleName);
    const pricePerUe = pricePerUeForModule(measure, moduleName);
    const totalUe = grouped.reduce((sum, s) => sum + (Number(s.ue_target) || 0), 0);
    return sections.map((s) => {
      if (canonicalModuleName(measure, s) !== moduleName) return s;
      const next = { ...s, module_name: moduleName };
      if (pricePerUe != null) next.price = Math.round(totalUe * pricePerUe * 100) / 100;
      return next;
    });
  };

  // Selbstheilung: beim Öffnen einer bestehenden Akte (oder wenn sich der Katalog-Preis/UE
  // geändert hat) wird der gespeicherte Preis jedes Moduls mit Preis/UE-Angabe neu aus der
  // tatsächlichen UE-Zahl berechnet — verhindert veraltete/leere Preise aus alten Akten.
  // Zusätzlich werden verwaiste Abschnitte entfernt, deren section_id nicht mehr im aktuellen
  // Katalog existiert (z. B. nach einer Katalog-Umbenennung) — sonst blieben alte + neu
  // ausgewählte Abschnitte gleichzeitig gespeichert und tauchten in der Übersicht doppelt auf.
  useEffect(() => {
    if (!open || !form.measure_id || measures.length === 0) return;
    const measure = measures.find((m) => m.measure_id === form.measure_id);
    if (!measure) return;
    setForm((f) => {
      const validIds = new Set((measure.sections || []).map((s) => s.section_id));
      const cleaned = f.sections.filter((s) => validIds.has(s.section_id));
      const moduleNames = [...new Set(cleaned.map((s) => canonicalModuleName(measure, s)))];
      let next = cleaned;
      for (const modName of moduleNames) next = recomputeModulePrice(next, measure, modName);
      const total = computeTotalUe(next, isLegacyOb && f.onboarding_ue, isLegacyOb && f.offboarding_ue);
      return { ...f, sections: next, total_ue: total };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.measure_id, measures]);

  // Toggle every section that belongs to a given module_name on/off.
  const toggleModule = (measure, moduleName, on) => {
    const moduleSections = (measure.sections || []).filter((s) => (s.module_name || "— Ohne Modul") === moduleName);
    setForm((f) => {
      let next = [...f.sections];
      if (on) {
        // add every section from that module (skip ones already present)
        for (const s of moduleSections) {
          if (!next.find((x) => x.section_id === s.section_id)) {
            next.push({
              section_id: s.section_id,
              module_name: s.module_name || null,
              name: s.name,
              ue_target: s.default_ue || 0,
              ue_completed: 0,
              coach_user_id: null,
              vz_tz: moduleSections[0]?.default_vz_tz || "VZ",
              duration: moduleSections[0]?.default_duration || null,
              price: null,
            });
          }
        }
        next = recomputeModulePrice(next, measure, moduleName);
      } else {
        const ids = new Set(moduleSections.map((s) => s.section_id));
        next = next.filter((x) => !ids.has(x.section_id));
      }
      const total = computeTotalUe(next, isLegacyOb && f.onboarding_ue, isLegacyOb && f.offboarding_ue);
      return { ...f, sections: next, total_ue: total };
    });
  };

  // Toggle a single section on/off. WICHTIG: Beim Abwählen (off) muss der Modul-Preis für die
  // VERBLEIBENDEN Abschnitte dieses Moduls neu berechnet werden — sonst bleibt der zuvor mit mehr
  // Abschnitten berechnete (zu hohe) Preis an den übrigen Abschnitten "kleben" (Bug: 80 statt 50 UE
  // im Vertrag, weil der alte Preis für alle 3 Abschnitte nach Abwahl von Abschnitt 3 nicht
  // neu für nur noch 2 Abschnitte berechnet wurde).
  const toggleSection = (measure, sectionMeta, on) => {
    const moduleName = sectionMeta.module_name || "— Ohne Modul";
    setForm((f) => {
      let next = [...f.sections];
      if (on) {
        if (!next.find((x) => x.section_id === sectionMeta.section_id)) {
          next.push({
            section_id: sectionMeta.section_id,
            module_name: sectionMeta.module_name || null,
            name: sectionMeta.name,
            ue_target: sectionMeta.default_ue || 0,
            ue_completed: 0,
            coach_user_id: null,
            vz_tz: sectionMeta.default_vz_tz || "VZ",
            duration: sectionMeta.default_duration || null,
            price: null,
          });
        }
      } else {
        next = next.filter((x) => x.section_id !== sectionMeta.section_id);
      }
      next = recomputeModulePrice(next, measure, moduleName);
      const total = computeTotalUe(next, isLegacyOb && f.onboarding_ue, isLegacyOb && f.offboarding_ue);
      return { ...f, sections: next, total_ue: total };
    });
  };

  const updateSection = (section_id, patch) => {
    setForm((f) => {
      let next = f.sections.map((s) => (s.section_id === section_id ? { ...s, ...patch } : s));
      if ("ue_target" in patch) {
        const changedSection = next.find((s) => s.section_id === section_id);
        const measure = measures.find((m) => m.measure_id === f.measure_id);
        if (measure) next = recomputeModulePrice(next, measure, canonicalModuleName(measure, changedSection));
      }
      const total = computeTotalUe(next, isLegacyOb && f.onboarding_ue, isLegacyOb && f.offboarding_ue);
      return { ...f, sections: next, total_ue: total };
    });
  };

  // Set a field (price, vz_tz, duration) on every section that belongs to this module —
  // used to build the per-module price table in the automatically generated contract.
  const updateModuleField = (moduleName, field, value) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s) => ((s.module_name || "— Ohne Modul") === moduleName ? { ...s, [field]: value } : s)),
    }));
  };
  const updateModulePrice = (moduleName, price) => updateModuleField(moduleName, "price", price);

  const toggleCoach = (uid) => {
    setForm((f) => {
      const has = f.coach_user_ids.includes(uid);
      const next = has ? f.coach_user_ids.filter((x) => x !== uid) : [...f.coach_user_ids, uid];
      if (next.length > 3) {
        toast.error("Maximal 3 Coaches pro Kunde");
        return f;
      }
      // Reset primary if removed
      let primary = f.primary_coach_id;
      if (!next.includes(primary)) primary = next[0] || "";
      return { ...f, coach_user_ids: next, primary_coach_id: primary };
    });
  };

  // Onboarding/Offboarding-UE sind vom Backoffice frei editierbar (mal 1, mal 2 UE) — bei jeder
  // Änderung muss das UE-Kontingent (total_ue) UND (falls die Maßnahme einen Preis/UE hat) der
  // Preis sofort neu berechnet werden.
  const updateOnboardingField = (field, value) => {
    setForm((f) => {
      const next = { ...f, [field]: value };
      const measure = measures.find((m) => m.measure_id === f.measure_id);
      const ppu = pricePerUeForMeasure(measure);
      if (ppu != null && (field === "onboarding_ue" || field === "offboarding_ue")) {
        next[field === "onboarding_ue" ? "onboarding_price" : "offboarding_price"] = Math.round((Number(value) || 0) * ppu * 100) / 100;
      }
      return { ...next, total_ue: computeTotalUe(next.sections, isLegacyOb && next.onboarding_ue, isLegacyOb && next.offboarding_ue) };
    });
  };

  const submit = async () => {
    if (!form.name) { toast.error("Bitte Name angeben"); return; }
    if (!editing && !form.email) { toast.error("Bitte E-Mail angeben — der Kunde bekommt einen Einladungslink"); return; }
    if (!editing && !form.acquired_by_user_id) { toast.error("Bitte angeben, wer den Kunden gewonnen hat"); return; }
    if (!form.customer_number.trim()) { toast.error("Bitte Kundennummer angeben"); return; }
    if (!form.measure_number.trim()) { toast.error("Bitte Maßnahmennummer angeben"); return; }
    if (!form.start_date || !form.end_date || form.end_date < form.start_date) {
      toast.error("Ungültiger Coaching-Zeitraum");
      return;
    }
    const hasSectionCoaches = form.sections.some((s) => !!s.coach_user_id);
    if (form.coach_user_ids.length === 0 && !hasSectionCoaches) {
      toast.error("Mindestens einen Coach zuordnen (global oder pro Abschnitt)");
      return;
    }
    if (!form.phone.trim()) { toast.error("Bitte Telefonnummer angeben"); return; }
    if (!form.kostentraeger.trim()) { toast.error("Bitte Kostenträger angeben"); return; }
    if (!form.ferien.trim()) { toast.error('Bitte Ferien im Maßnahmezeitraum angeben (oder "Keine")'); return; }
    if (!form.gesetzl_feiertage.trim()) { toast.error('Bitte gesetzl. Feiertage im Maßnahmezeitraum angeben (oder "Keine")'); return; }
    if (isLegacyOb) {
      if (!(Number(form.onboarding_ue) > 0)) { toast.error("Onboarding-UE muss größer als 0 sein — jeder Kunde muss ein Onboarding durchlaufen"); return; }
      if (!(Number(form.offboarding_ue) > 0)) { toast.error("Offboarding-UE muss größer als 0 sein — jeder Kunde muss ein Offboarding durchlaufen"); return; }
    }
    if (!form.measure_id) { toast.error("Bitte eine AVGS-Maßnahme wählen"); return; }
    if (form.sections.length === 0) { toast.error("Bitte mindestens ein Modul auswählen"); return; }
    for (const s of form.sections) {
      if (!s.ue_target) { toast.error(`Modul "${s.module_name || s.name}": Bitte UE angeben`); return; }
    }
    if (isLegacyOb) {
      if (form.onboarding_price === null || form.onboarding_price === undefined || form.onboarding_price === "") { toast.error("Onboarding: Bitte Preis angeben"); return; }
      if (form.offboarding_price === null || form.offboarding_price === undefined || form.offboarding_price === "") { toast.error("Offboarding: Bitte Preis angeben"); return; }
    }
    const moduleNames = [...new Set(form.sections.map((s) => s.module_name || s.name))];
    for (const mod of moduleNames) {
      const rep = form.sections.find((s) => (s.module_name || s.name) === mod);
      if (rep.price === null || rep.price === undefined || rep.price === "") { toast.error(`Modul "${mod}": Bitte Preis angeben`); return; }
      if (!rep.vz_tz) { toast.error(`Modul "${mod}": Bitte VZ/TZ angeben`); return; }
      if (!rep.duration || !rep.duration.trim()) { toast.error(`Modul "${mod}": Bitte Dauer angeben`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || undefined,
        phone: form.phone || "",
        street: form.street || "",
        postal_code: form.postal_code || "",
        city: form.city || "",
        customer_number: form.customer_number || null,
        measure_number: form.measure_number || null,
        start_date: form.start_date,
        end_date: form.end_date,
        total_ue: parseFloat(form.total_ue) || 0,
        coach_user_ids: form.coach_user_ids,
        primary_coach_id: form.primary_coach_id || null,
        notes: form.notes || null,
        internal_notes: form.internal_notes || null,
        measure_id: form.measure_id || null,
        sections: form.sections || [],
        birth_date: form.birth_date || null,
        kostentraeger: form.kostentraeger || null,
        arbeitsvermittler_email: form.arbeitsvermittler_email || null,
        ferien: form.ferien || null,
        gesetzl_feiertage: form.gesetzl_feiertage || null,
        capacity_hours_per_week: form.capacity_hours_per_week === "" ? null : parseFloat(form.capacity_hours_per_week),
        onboarding_ue: parseFloat(form.onboarding_ue) || 0,
        offboarding_ue: parseFloat(form.offboarding_ue) || 0,
        onboarding_user_id: form.onboarding_user_id || null,
        offboarding_user_id: form.offboarding_user_id || null,
        onboarding_price: parseFloat(form.onboarding_price),
        offboarding_price: parseFloat(form.offboarding_price),
        acquired_by_user_id: form.acquired_by_user_id || undefined,
      };
      if (!editing) payload.email = form.email;
      const { data } = editing
        ? await api.put(`/clients/${initialData.client_id}`, payload)
        : await api.post("/clients", payload);
      toast.success(editing ? "Kunde aktualisiert" : "Kunde angelegt & eingeladen");
      onSaved?.(editing ? data : data.client);
      if (!editing && data.invite_link) {
        setInviteResult(data);
      } else {
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const copyInvite = async () => {
    if (!inviteResult?.invite_link) return;
    try {
      await navigator.clipboard.writeText(inviteResult.invite_link);
      toast.success("Einladungslink kopiert");
    } catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none border border-slate-200 p-0" data-testid="client-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
            {editing ? "Kunde bearbeiten" : "Neuer Kunde"}
          </div>
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
            AVGS-Teilnehmer
          </DialogTitle>
          <DialogDescription className="sr-only">Lege einen neuen AVGS-Kunden an oder bearbeite einen bestehenden.</DialogDescription>
        </DialogHeader>

        {!editing && !inviteResult && <WizardStepper step={step} onGo={setStep} />}
        <div className="px-8 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
          <div className={show(0) ? "space-y-5" : "hidden"}>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Name *</label>
            <input data-testid="client-name-input" className="input-tech" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className={editing ? "grid grid-cols-2 gap-6" : "space-y-5"}>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                {editing ? "E-Mail" : "E-Mail * (Einladung wird verschickt)"}
              </label>
              <input data-testid="client-email-input" type="email" className="input-tech font-mono" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <p className="text-xs text-slate-500 mt-2">{editing ? "Änderung ändert auch den Login des Kunden." : "Der Kunde bekommt einen Einladungslink zum Passwort-Setzen."}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Telefon *</label>
              <input data-testid="client-phone-input" type="tel" className="input-tech font-mono" placeholder="+49 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>

          {/* Adresse — für Rechnungen an Jobcenter / Arbeitsagentur */}
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Adresse (für Rechnung) <span className="font-normal normal-case tracking-normal text-slate-400">– kann später nachgetragen werden</span></label>
            <input
              data-testid="client-street-input"
              className="input-tech"
              placeholder="Straße und Hausnummer"
              value={form.street}
              onChange={(e) => setForm({ ...form, street: e.target.value })}
            />
            <div className="grid grid-cols-[130px_1fr] gap-3 mt-3">
              <input
                data-testid="client-postal-input"
                className="input-tech font-mono"
                placeholder="PLZ"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              />
              <input
                data-testid="client-city-input"
                className="input-tech"
                placeholder="Ort"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Wird auf der Anwesenheitsliste und in Rechnungen an das Jobcenter / die Arbeitsagentur ausgegeben.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-5">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-3">
              Leistungsträger — Kontaktdaten (für Abschluss-Rechnung)
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kostenträger / Leistungsträger *</label>
                <input data-testid="client-kostentraeger-input" className="input-tech" placeholder="z.B. Jobcenter Berlin Mitte" value={form.kostentraeger} onChange={(e) => setForm({ ...form, kostentraeger: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">E-Mail Arbeitsvermittler*in (optional)</label>
                <input data-testid="client-arbeitsvermittler-email-input" type="email" className="input-tech" placeholder="vermittler@jobcenter.de" value={form.arbeitsvermittler_email} onChange={(e) => setForm({ ...form, arbeitsvermittler_email: e.target.value })} />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Die Abschluss-Rechnung nach Ende der Maßnahme geht automatisch an diese E-Mail-Adresse (Leistungsträger) — nicht an den Kunden.
            </p>
          </div>
          </div>
          <div className={show(1) ? "space-y-5" : "hidden"}>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kundennummer *</label>
              <input data-testid="client-customer-input" className="input-tech font-mono" required value={form.customer_number} onChange={(e) => setForm({ ...form, customer_number: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Maßnahmennummer *</label>
              <input data-testid="client-measure-input" className="input-tech font-mono" required value={form.measure_number} onChange={(e) => setForm({ ...form, measure_number: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
              Wer hat den Kunden gewonnen?{!editing && " *"}
            </label>
            <select
              data-testid="client-acquired-by-select"
              className="input-tech"
              required={!editing}
              value={form.acquired_by_user_id}
              onChange={(e) => setForm({ ...form, acquired_by_user_id: e.target.value })}
            >
              <option value="">— Mitarbeiter wählen —</option>
              {(allStaff || allCoaches || []).map((c) => (
                <option key={c.user_id} value={c.user_id}>{c.name}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-2">
              Zählt automatisch zur KPI "Anzahl AVGS-UES gewonnen" der ausgewählten Person.
            </p>
          </div>
          </div>

          {/* --- Maßnahme + Abschnitte --- */}
          <div className={show(2) ? "" : "hidden"}>
          <div className="border-t border-slate-200 pt-5">
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
              AVGS-Maßnahme *
            </label>
            <select
              data-testid="client-measure-select"
              className="input-tech"
              value={form.measure_id}
              onChange={(e) => pickMeasure(e.target.value)}
            >
              <option value="">— keine Maßnahme aus Katalog —</option>
              {measures.map((m) => (
                <option key={m.measure_id} value={m.measure_id}>{`${m.name} · ${m.sections.length} Abschnitte`}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-2">
              Wähle eine Maßnahme → dann kreuze nur die <b>Module & Abschnitte</b> an, die im Gutschein dieses Kunden enthalten sind.
            </p>

            {form.measure_id && (() => {
              const measure = measures.find((x) => x.measure_id === form.measure_id);
              if (!measure) return null;
              // Group sections by module_name preserving order of first occurrence
              const moduleOrder = [];
              const moduleMap = {};
              for (const s of (measure.sections || [])) {
                const key = s.module_name || "— Ohne Modul";
                if (!(key in moduleMap)) { moduleMap[key] = []; moduleOrder.push(key); }
                moduleMap[key].push(s);
              }
              const selectedIds = new Set(form.sections.map((x) => x.section_id));
              return (
                <div className="mt-4 space-y-3" data-testid="client-modules-picker">
                  {isLegacyOb && (
                  <div className="border border-amber-200" data-testid="onboarding-block">
                    <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                        Onboarding * <span className="text-slate-400 normal-case font-normal">— Pflicht für jeden Kunden, nicht im Vertrag, nur intern vom UE-Kontingent abgezogen</span>
                      </div>
                    </div>
                    <div className="px-3 py-2.5 grid grid-cols-1 sm:grid-cols-[110px_1fr_140px] gap-2">
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">UE (1 UE = 45 Min.)</label>
                        <input
                          data-testid="client-onboarding-ue-input"
                          type="number" min="0.5" step="0.5"
                          className="input-tech font-mono text-sm"
                          value={form.onboarding_ue}
                          onChange={(e) => updateOnboardingField("onboarding_ue", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Zuständig</label>
                        <select
                          data-testid="client-onboarding-user-select"
                          className="input-tech text-sm"
                          value={form.onboarding_user_id}
                          onChange={(e) => updateOnboardingField("onboarding_user_id", e.target.value)}
                        >
                          <option value="">— nicht zugewiesen —</option>
                          {(allStaff || allCoaches || []).map((c) => (
                            <option key={c.user_id} value={c.user_id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Preis (intern, nicht im Vertrag)</label>
                        {pricePerUeForMeasure(measure) != null ? (
                          <>
                            <div data-testid="client-onboarding-price-readonly" className="input-tech font-mono text-sm bg-slate-100 text-slate-500">
                              {Number(form.onboarding_price || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                            </div>
                            <p className="text-[9px] text-slate-400 mt-0.5">= UE × {pricePerUeForMeasure(measure).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
                          </>
                        ) : (
                          <input
                            data-testid="client-onboarding-price-input"
                            type="number" min="0" step="0.01"
                            className="input-tech font-mono text-sm"
                            value={form.onboarding_price ?? ""}
                            onChange={(e) => updateOnboardingField("onboarding_price", e.target.value === "" ? null : parseFloat(e.target.value))}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  )}
                  {moduleOrder.map((mod) => {
                    const secs = moduleMap[mod];
                    const activeCount = secs.filter((s) => selectedIds.has(s.section_id)).length;
                    const allOn = activeCount === secs.length;
                    const someOn = activeCount > 0 && !allOn;
                    return (
                      <div key={mod} className="border border-slate-200" data-testid={`module-block-${mod}`}>
                        <label className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 flex-wrap">
                          <input
                            type="checkbox"
                            checked={allOn}
                            ref={(el) => { if (el) el.indeterminate = someOn; }}
                            onChange={(e) => toggleModule(measure, mod, e.target.checked)}
                            className="accent-[#00CCC9] w-4 h-4"
                            data-testid={`toggle-module-${mod}`}
                          />
                          <div className="flex-1 min-w-[140px]">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 truncate">{mod}</div>
                            <div className="text-[11px] text-slate-500 font-mono">
                              {activeCount} / {secs.length} Abschnitte · {secs.filter((s) => selectedIds.has(s.section_id)).reduce((sum, s) => sum + (form.sections.find((x) => x.section_id === s.section_id)?.ue_target || 0), 0)} UE
                            </div>
                          </div>
                          {activeCount > 0 && (
                            <div onClick={(e) => e.preventDefault()} className="flex items-center gap-3 flex-wrap w-full pl-7 mt-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-bold uppercase tracking-overline text-slate-500">VZ/TZ *</span>
                                <select
                                  value={form.sections.find((x) => (x.module_name || "— Ohne Modul") === mod)?.vz_tz || "VZ"}
                                  onChange={(e) => updateModuleField(mod, "vz_tz", e.target.value)}
                                  className="input-tech text-xs w-16"
                                  data-testid={`module-vztz-${mod}`}
                                >
                                  <option value="VZ">VZ</option>
                                  <option value="TZ">TZ</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-bold uppercase tracking-overline text-slate-500">Dauer *</span>
                                <input
                                  value={form.sections.find((x) => (x.module_name || "— Ohne Modul") === mod)?.duration || ""}
                                  onChange={(e) => updateModuleField(mod, "duration", e.target.value)}
                                  placeholder="8 Wochen"
                                  className="input-tech text-xs w-20"
                                  data-testid={`module-duration-${mod}`}
                                />
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-bold uppercase tracking-overline text-slate-500">Preis (€) *</span>
                                {(() => {
                                  const pricePerUe = pricePerUeForModule(measure, mod);
                                  const priceVal = form.sections.find((x) => (x.module_name || "— Ohne Modul") === mod)?.price ?? "";
                                  if (pricePerUe != null) {
                                    return (
                                      <span className="flex items-center gap-1.5">
                                        <input type="number" value={priceVal} readOnly disabled
                                          className="input-tech font-mono text-xs w-24 bg-slate-100 text-slate-500 cursor-not-allowed"
                                          data-testid={`module-price-${mod}`} title="Automatisch berechnet: UE × Preis/UE" />
                                        <span className="text-[9px] text-slate-400 font-mono">= UE × {pricePerUe.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                                      </span>
                                    );
                                  }
                                  return (
                                    <input
                                      type="number" min="0" step="0.01"
                                      value={priceVal}
                                      onChange={(e) => updateModulePrice(mod, e.target.value === "" ? null : parseFloat(e.target.value))}
                                      placeholder="0,00"
                                      className="input-tech font-mono text-xs w-24"
                                      data-testid={`module-price-${mod}`}
                                    />
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </label>
                        <div className="divide-y divide-slate-100">
                          {secs.map((s) => {
                            const active = selectedIds.has(s.section_id);
                            const secForm = form.sections.find((x) => x.section_id === s.section_id);
                            return (
                              <div key={s.section_id} className="px-3 py-2" data-testid={`section-block-${s.section_id}`}>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={(e) => toggleSection(measure, s, e.target.checked)}
                                    className="accent-[#00CCC9] w-4 h-4"
                                    data-testid={`toggle-section-${s.section_id}`}
                                  />
                                  <div className="text-sm text-slate-900 flex-1 min-w-0 truncate">{s.name}</div>
                                  <span className="text-[10px] text-slate-400 font-mono">Vorschlag: {s.default_ue} UE</span>
                                </label>
                                {active && (
                                  <div className="mt-2 pl-6 grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-2">
                                    <div>
                                      <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">UE</label>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={secForm?.ue_target ?? 0}
                                        onChange={(e) => updateSection(s.section_id, { ue_target: parseFloat(e.target.value) || 0 })}
                                        className="input-tech font-mono text-sm"
                                        data-testid={`section-ue-${s.section_id}`}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Coach</label>
                                      <select
                                        value={secForm?.coach_user_id || ""}
                                        onChange={(e) => updateSection(s.section_id, { coach_user_id: e.target.value || null })}
                                        className="input-tech text-sm"
                                        data-testid={`section-coach-${s.section_id}`}
                                      >
                                        <option value="">— noch nicht zugewiesen —</option>
                                        {allCoaches.map((c) => (
                                          <option key={c.user_id} value={c.user_id}>{c.name}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {isLegacyOb && (
                  <div className="border border-amber-200" data-testid="offboarding-block">
                    <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                        Offboarding * <span className="text-slate-400 normal-case font-normal">— Pflicht für jeden Kunden, nicht im Vertrag, nur intern vom UE-Kontingent abgezogen</span>
                      </div>
                    </div>
                    <div className="px-3 py-2.5 grid grid-cols-1 sm:grid-cols-[110px_1fr_140px] gap-2">
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">UE (1 UE = 45 Min.)</label>
                        <input
                          data-testid="client-offboarding-ue-input"
                          type="number" min="0.5" step="0.5"
                          className="input-tech font-mono text-sm"
                          value={form.offboarding_ue}
                          onChange={(e) => updateOnboardingField("offboarding_ue", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Zuständig</label>
                        <select
                          data-testid="client-offboarding-user-select"
                          className="input-tech text-sm"
                          value={form.offboarding_user_id}
                          onChange={(e) => updateOnboardingField("offboarding_user_id", e.target.value)}
                        >
                          <option value="">— nicht zugewiesen —</option>
                          {(allStaff || allCoaches || []).map((c) => (
                            <option key={c.user_id} value={c.user_id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Preis (intern, nicht im Vertrag)</label>
                        {pricePerUeForMeasure(measure) != null ? (
                          <>
                            <div data-testid="client-offboarding-price-readonly" className="input-tech font-mono text-sm bg-slate-100 text-slate-500">
                              {Number(form.offboarding_price || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                            </div>
                            <p className="text-[9px] text-slate-400 mt-0.5">= UE × {pricePerUeForMeasure(measure).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
                          </>
                        ) : (
                          <input
                            data-testid="client-offboarding-price-input"
                            type="number" min="0" step="0.01"
                            className="input-tech font-mono text-sm"
                            value={form.offboarding_price ?? ""}
                            onChange={(e) => updateOnboardingField("offboarding_price", e.target.value === "" ? null : parseFloat(e.target.value))}
                          />
                        )}
                      </div>
                    </div>
                    <p className="px-3 pb-2 text-[10px] text-slate-500">
                      Zuständig kann auch jemand aus dem Backoffice sein, nicht zwingend der Coach — zählt für diese Person in der Kapazitäts-Vorschau (/kapazitaet) mit.
                    </p>
                  </div>
                  )}
                  {!isLegacyOb && (
                  <div className="border border-slate-200 bg-slate-50" data-testid="ob-responsible-block">
                    <div className="px-3 py-2.5 border-b border-slate-200">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                        Onboarding/Offboarding <span className="text-slate-400 normal-case font-normal">— kein eigenes UE-Ziel mehr, wird bei Terminen als Abschnitt gewählt und automatisch vom UE-Kontingent abgezogen. Zuständige Person nur zur Orientierung.</span>
                      </div>
                    </div>
                    <div className="px-3 py-2.5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Onboarding zuständig</label>
                        <select
                          data-testid="client-onboarding-user-select"
                          className="input-tech text-sm"
                          value={form.onboarding_user_id}
                          onChange={(e) => updateOnboardingField("onboarding_user_id", e.target.value)}
                        >
                          <option value="">— nicht zugewiesen —</option>
                          {(allStaff || allCoaches || []).map((c) => (
                            <option key={c.user_id} value={c.user_id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[9px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Offboarding zuständig</label>
                        <select
                          data-testid="client-offboarding-user-select"
                          className="input-tech text-sm"
                          value={form.offboarding_user_id}
                          onChange={(e) => updateOnboardingField("offboarding_user_id", e.target.value)}
                        >
                          <option value="">— nicht zugewiesen —</option>
                          {(allStaff || allCoaches || []).map((c) => (
                            <option key={c.user_id} value={c.user_id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  )}
                  <div className="p-3 bg-slate-900 text-white text-xs font-mono flex justify-between" data-testid="sections-summary">
                    <span className="uppercase tracking-overline text-[10px] font-bold">Summe Abschnitte{isLegacyOb ? " + Onboarding/Offboarding" : ""}</span>
                    <span data-testid="sections-summary-values">
                      {form.sections.length} Abschnitte · {computeTotalUe(form.sections, isLegacyOb ? form.onboarding_ue : 0, isLegacyOb ? form.offboarding_ue : 0)} UE gesamt
                      {(() => {
                        const seen = new Set();
                        let total = isLegacyOb ? Number(form.onboarding_price || 0) + Number(form.offboarding_price || 0) : 0;
                        for (const s of form.sections) {
                          const key = s.module_name || "— Ohne Modul";
                          if (!seen.has(key) && s.price) { seen.add(key); total += Number(s.price); }
                        }
                        return total > 0 ? ` · ${total.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € gesamt (intern)` : "";
                      })()}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
          </div>
          <div className={show(1) ? "" : "hidden"}>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Coaching-Start *</label>
              <GermanDateInput data-testid="client-start-input"  className="input-tech font-mono" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Coaching-Ende *</label>
              <GermanDateInput data-testid="client-end-input"  className="input-tech font-mono" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          </div>
          <div className={show(2) ? "space-y-5" : "hidden"}>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">UE-Kontingent{isAdmin ? "" : " (automatisch)"}</label>
            <input
              data-testid="client-total-ue-input"
              type="number"
              step="1"
              min="0"
              readOnly={!isAdmin}
              className={`input-tech font-mono ${isAdmin ? "" : "bg-slate-100 text-slate-700 cursor-not-allowed"}`}
              value={form.total_ue}
              onChange={(e) => isAdmin && setForm({ ...form, total_ue: e.target.value })}
              title={isAdmin ? "Wird aus den Abschnitten berechnet, kann als Admin aber jederzeit korrigiert werden (z. B. Nachtrag oder Aufstockung durch den Träger)" : "Wird automatisch aus der Summe der ausgewählten Abschnitte plus Onboarding/Offboarding berechnet"}
            />
            <p className="text-xs text-slate-500 mt-2">
              Wird automatisch aus der <b>Summe der UE aller ausgewählten Abschnitte</b>{isLegacyOb ? <> + <b>Onboarding/Offboarding-UE</b> (oben in der Modulliste)</> : ""} berechnet –
              {isAdmin ? <> als Admin kannst du den Wert hier <b>jederzeit anpassen</b>, etwa bei einer Aufstockung oder einem vergessenen Nachtragstermin.</> : " nur ein Admin kann den Wert anpassen."} Wird bei jedem unterschriebenen Termin abgezogen.
              {!isLegacyOb && " Onboarding/Offboarding-Termine zählen ohne eigenes Ziel automatisch mit dazu."}
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kapazität überschreiben (Std./Woche)</label>
            <input data-testid="client-capacity-override-input" type="number" step="0.5" min="0" placeholder="Standard: 6" className="input-tech font-mono" value={form.capacity_hours_per_week} onChange={(e) => setForm({ ...form, capacity_hours_per_week: e.target.value })} />
            <p className="text-xs text-slate-500 mt-2">Nur für die Coach-Kapazitäts-Vorschau (Management). Standard-Annahme: 6 Std./Woche (3 Termine à 2 Std.). Leer lassen, wenn Standard passt.</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
              Zugeordnete Coaches * (max. 3)
            </label>
            <div className="border border-slate-200 divide-y divide-slate-100" data-testid="client-coaches-list">
              {allCoaches.length === 0 && <div className="p-3 text-sm text-slate-500">Keine Coaches verfügbar</div>}
              {allCoaches.map((c) => {
                const isSelected = form.coach_user_ids.includes(c.user_id);
                const isPrimary = form.primary_coach_id === c.user_id && isSelected;
                return (
                  <div key={c.user_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                    <label className="flex items-center gap-3 flex-1 cursor-pointer">
                      <input
                        data-testid={`client-coach-${c.user_id}`}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCoach(c.user_id)}
                      />
                      <span className="text-sm text-slate-900">{c.name}</span>
                      <span className="text-xs font-mono text-slate-500">{c.email}</span>
                    </label>
                    {isSelected && (
                      <button
                        type="button"
                        data-testid={`client-primary-${c.user_id}`}
                        onClick={() => setForm({ ...form, primary_coach_id: c.user_id })}
                        className={`text-[10px] px-2 py-1 border transition-colors ${
                          isPrimary
                            ? "bg-slate-900 text-white border-slate-900"
                            : "border-slate-300 text-slate-500 hover:bg-slate-100"
                        }`}
                        title="Haupt-Coach setzen"
                      >
                        {isPrimary ? "★ Haupt-Coach" : "Als Haupt-Coach"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Der <b>Haupt-Coach</b> ist der primäre Ansprechpartner. Zusätzliche Coaches (z.B. für Urlaubsvertretung) können den Kunden ebenfalls betreuen und sehen alle Termine.
            </p>
          </div>
          </div>
          <div className={show(1) ? "space-y-5" : "hidden"}>
          <div className="border-t border-slate-200 pt-6">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-3">
              Vertragsdaten (für die automatische Vertrags-Erstellung)
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Geburtsdatum (optional)</label>
                <GermanDateInput data-testid="client-birth-date-input" className="input-tech font-mono" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Gesetzl. Feiertage im Maßnahmezeitraum *</label>
                <input data-testid="client-feiertage-input" className="input-tech" placeholder="z.B. 03.10., 25./26.12.2026 (oder „Keine“)" value={form.gesetzl_feiertage} onChange={(e) => setForm({ ...form, gesetzl_feiertage: e.target.value })} />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Ferien im Maßnahmezeitraum *</label>
              <input data-testid="client-ferien-input" className="input-tech" placeholder="z.B. 23.12.2026 – 06.01.2027 (oder „Keine“)" value={form.ferien} onChange={(e) => setForm({ ...form, ferien: e.target.value })} />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Diese Angaben fließen automatisch in den nativ generierten Vertrag ein. Ferien und Feiertage sind Pflicht (bei „keine“ einfach „Keine“ eintragen) — nur Geburtsdatum ist optional. Modul-UE, VZ/TZ, Dauer und Preis werden direkt bei den Abschnitten oben gepflegt.
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Notizen (öffentlich)</label>
            <input data-testid="client-notes-input" className="input-tech" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <p className="text-xs text-slate-500 mt-2">
              Interne Coach-Notizen 🔒 lassen sich direkt in der Kunden-Akte bearbeiten (Karte "Interne Notizen").
            </p>
          </div>
          </div>
          {!editing && step === 3 && <WizardReview form={form} measures={measures} coaches={allStaff || allCoaches || []} onEdit={inviteResult ? null : setStep} />}
        </div>

        <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
          {inviteResult ? (
            <>
              <div className="flex-1 text-xs font-mono text-slate-700 bg-slate-50 p-3 border border-slate-200 break-all" data-testid="client-invite-link">
                {inviteResult.invite_link}
              </div>
              <button data-testid="client-invite-copy" onClick={copyInvite} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">Kopieren</button>
              <button data-testid="client-invite-done" onClick={() => onOpenChange(false)} className="px-6 py-3 text-sm font-medium border border-slate-300">Fertig</button>
            </>
          ) : !editing && step < 3 ? (
            <>
              <button data-testid="client-cancel" onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))} className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                {step === 0 ? "Abbrechen" : "Zurück"}
              </button>
              <button data-testid="client-wizard-next" onClick={next} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                Weiter: {WIZARD_STEPS[step + 1]}
              </button>
            </>
          ) : (
            <>
              <button data-testid={editing ? "client-cancel" : "client-wizard-back"} onClick={() => (editing ? onOpenChange(false) : setStep(2))} className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                {editing ? "Abbrechen" : "Zurück"}
              </button>
              <button data-testid="client-save" onClick={submit} disabled={saving} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
                {saving ? "Speichert…" : editing ? "Aktualisieren" : "Kunde anlegen & einladen"}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
